import { partnerBlocks } from '@/features/marketing'
import type { TrainingType } from '@/types/database'

export const MARQUEE_LOGOS = [...partnerBlocks, ...partnerBlocks, ...partnerBlocks, ...partnerBlocks]

export const SERVICES = [
  {
    id: 'lms',
    title: 'LMS',
    subtitle: 'Learning Management System',
    description:
      'A structured digital environment to manage, deliver, and track online training programs — tailored for professional and corporate learning.',
    items: ['Online Training', 'Formation'],
    icon: 'menu_book',
    glow: 'rgba(168,85,247,0.22)',
    border: 'rgba(168,85,247,0.3)',
  },
  {
    id: 'lxp',
    title: 'LXP',
    subtitle: 'Learning Experience Platform — Powered by AI',
    description:
      'An AI-driven learning experience that adapts to each learner\'s profile, skills gaps, and goals — delivering personalized intelligence curves at scale.',
    items: ['Gamification', 'Diagnostic', 'Adaptive Learning'],
    icon: 'psychology',
    glow: 'rgba(6,182,212,0.22)',
    border: 'rgba(6,182,212,0.3)',
  },
  {
    id: 'bootcamp',
    title: 'Bootcamp',
    subtitle: 'Intensive Training Programs',
    description:
      'Hands-on, execution-first programs delivered in-person or on-site for teams — built for real workflow integration from day one.',
    items: ['Presential (In-person)', 'Company Training (B2B)'],
    icon: 'rocket_launch',
    glow: 'rgba(236,72,153,0.18)',
    border: 'rgba(236,72,153,0.25)',
  },
]

export const TYPE_META: Record<TrainingType, { icon: string; badge: string }> = {
  bootcamp: { icon: 'hub', badge: 'Bootcamp' },
  master_class: { icon: 'auto_awesome', badge: 'Master Class' },
  team_training: { icon: 'groups', badge: 'Team Training' },
}

export function deriveDuration(start: string | null, end: string | null): string | null {
  if (!start || !end) return null
  const weeks = Math.round(
    (new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24 * 7),
  )
  return weeks > 0 ? `${weeks} week${weeks > 1 ? 's' : ''}` : null
}

export const PROGRAM_FEATURES = [
  {
    id: 'mentors',
    icon: 'groups',
    title: 'Agentic Mentorship',
    desc: 'AI-native practitioners diagnose your workflow gaps and deploy targeted interventions — not generic lectures.',
  },
  {
    id: 'schedule',
    icon: 'calendar_month',
    title: 'Execution-First Design',
    desc: 'Live diagnostic sessions + async modules built for operators, not students. No career pause required.',
  },
  {
    id: 'projects',
    icon: 'work_history',
    title: 'Workflow Integration',
    desc: 'Graduate with live AI integrations deployed inside your actual workflows — not portfolio demos.',
  },
]

export const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Diagnose',
    desc: 'We run a deep diagnostic on your role, team, and workflow — mapping the exact gaps AI can close for maximum execution velocity.',
  },
  {
    step: '02',
    title: 'Deploy',
    desc: 'Your Custom Learning Curve is generated and deployed — agentic sessions, applied modules, and live workflow integration. No one-size-fits-all.',
  },
  {
    step: '03',
    title: 'Operate',
    desc: 'Week one, you execute. AI becomes part of your operating system — not a side skill. Industrial-scale output, professional-scale schedule.',
  },
]

export type BubbleVariant = 'purple' | 'cyan'

export const BUBBLES: {
  id: string
  name: string
  role: string
  tag: string
  quote: string
  variant: BubbleVariant
  pos: { top: string; left: string }
  rotate: string
  animDelay: string
}[] = [
  {
    id: 'pro1',
    name: 'Samir E.',
    role: 'Senior Manager',
    tag: 'Professional',
    quote: "My team uses AI daily — I don't even know where to start. I feel left behind in my own department.",
    variant: 'purple',
    pos: { top: '3%', left: '2%' },
    rotate: '-2deg',
    animDelay: '0s',
  },
  {
    id: 'pro2',
    name: 'Leila B.',
    role: 'CFO · Finance',
    tag: 'Professional',
    quote: "I'm afraid the next promotion will go to someone who just knows AI better than me.",
    variant: 'purple',
    pos: { top: '1%', left: '58%' },
    rotate: '1.5deg',
    animDelay: '1.4s',
  },
  {
    id: 'biz1',
    name: 'Karim A.',
    role: 'Business Owner · Retail',
    tag: 'Business Owner',
    quote: "I spend hours on tasks AI could do in minutes. My competitors are pulling ahead.",
    variant: 'cyan',
    pos: { top: '37%', left: '0%' },
    rotate: '-1.5deg',
    animDelay: '0.7s',
  },
  {
    id: 'biz2',
    name: 'Nadia M.',
    role: 'Entrepreneur · Services',
    tag: 'Business Owner',
    quote: "I don't know which AI tools are worth investing in. I can't afford to get it wrong.",
    variant: 'cyan',
    pos: { top: '47%', left: '65%' },
    rotate: '2deg',
    animDelay: '2.1s',
  },
  {
    id: 'pro3',
    name: 'Youssef T.',
    role: 'Operations Director',
    tag: 'Professional',
    quote: "Every training I find is either too technical or built for students. Nothing fits my schedule.",
    variant: 'purple',
    pos: { top: '68%', left: '6%' },
    rotate: '-1deg',
    animDelay: '3s',
  },
]
